import { Link } from "react-router-dom";
import { Box, Switch, styled } from "@mui/material";
import * as Yup from "yup";

import { formatPhone } from "./utils";
import type { FieldRule } from "../types";

export const cashbackFieldRules = {
    fields: [
        { l: `Cash Back Amount`, v: `cashback`, htmlName: `cashback`, id: `cashback` },
        { l: `Cash Back Method`, v: `method`, htmlName: `method`, id: `method` },
        { l: `PayPal Email`, v: `email`, htmlName: `email`, id: `email` },
        { l: `Venmo Phone`, v: `phone`, id: `phone`, regex: /[^.0-9]/g, htmlName: `phone` },
    ],
    validator: Yup.object().shape({
        // (prior: Yup.string(`Please enter a valid amwount`) — the argument was ignored by yup's string(); removed to satisfy its no-arg signature without changing behavior)
        cashback: Yup.string().required(`Please enter a valid amount`),
        method: Yup.string().oneOf([`PayPal`, `Venmo`]),
        email: Yup.string().when(`method`, {
            is: `PayPal`,
            then: () =>
                Yup.string().email(`Please enter a valid email address`).required(`Please enter a valid email address`),
        }),
        phone: Yup.string().when(`method`, {
            is: `Venmo`,
            then: () =>
                Yup.string()
                    .transform(v => v.replace(/[^0-9]/g, ``))
                    .min(10)
                    .max(11)
                    .required(`Please enter a valid phone number`),
        }),
    }),
};

export const fieldRules: Record<string, FieldRule> = {
    paypal: {
        title: `Paypal`,
        fields: [{ l: `PayPal Email Address`, v: `paypal-email`, t: `email` }],
        validator: Yup.object().shape({
            "paypal-email": Yup.string().email(`Please enter a valid email`).max(255).required(`Email is required`),
        }),
        updateWithSettings: true,
    },
    venmo: {
        title: `Venmo`,
        fields: [{ l: `Venmo Phone Number`, v: `venmo-phone`, regex: /[^.0-9]/g, visualFilter: formatPhone }],
        validator: Yup.object().shape({
            "venmo-phone": Yup.string()
                .min(10, `Please enter full phone number`)
                .max(11, `Please enter valid phone number`)
                .required(`Phone number is required`),
        }),
        updateWithSettings: true,
    },
    name: {
        title: `Update Your Name`,
        fields: [
            { l: `First Name`, v: `firstName`, htmlName: `firstName` },
            { l: `Last Name`, v: `lastName`, htmlName: `lastName` },
        ],
        validator: Yup.object().shape({
            firstName: Yup.string().required(`Please enter a first name`).min(2, `Please enter a first name`),
            lastName: Yup.string().required(`Please enter a last name`).min(2, `Please enter a last name`),
        }),
    },
    address: {
        title: `Update Your Street Address`,
        fields: [
            { l: `Street Address`, v: `address1`, htmlName: `address`, id: `lookupaddress` },
            { l: `Unit/Apartment/Building`, v: `address2`, htmlName: `address2`, id: `lookupaddress2` },
            { l: `City`, v: `city`, id: `city`, htmlName: `city`, width: 350, mr: 2 },
            { l: `State`, v: `state`, id: `state`, htmlName: `state`, width: 75, mr: 2 },
            { l: `Zip`, v: `zipcode`, id: `zipcode`, htmlName: `zipcode`, width: 125 },
        ],
        validator: Yup.object().shape({
            city: Yup.string().required(`Please select your address`),
            state: Yup.string().required(`Please select your address`),
            zipcode: Yup.string().required(`Please select your address`),
            address1: Yup.string().required().min(12, `Please enter your address`),
        }),
    },
};

export const LiLink = styled(Link)<{ color?: string; nohover?: boolean; variant?: string }>(
    ({ color, theme, sx, nohover }) => ({
        textDecoration: `none`,
        padding: `5px 0`,
        color: color || theme.palette.account?.accent || `black`,
        "&.fullheight": {
            height: `100%`,
            display: `inline-block`,
        },

        "&.dblock": {
            display: `block`,
        },
        "&:visited": {
            color: color || theme.palette.account?.accent || `black`,
        },

        "&:hover": {
            color: nohover ? color : `#303030`,
        },

        "&.bghover": {
            padding: 0,
            paddingLeft: `16px`,
            "&:hover": {
                color: `#303030`,
                background: `#eee`,
            },
        },
        ...(sx as Record<string, string>),
    })
);

export const AppWrap = styled(Box)(() => ({
    maxWidth: `1440px`,
    margin: `0 auto`,
    minHeight: `780px`,
}));

export const GreenSwitch = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
        color: theme.palette.account.accent,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: `${theme.palette.account.accent}80`,
    },
}));

export const missingTransactionFieldRules = {
    fields: [
        { l: `Order ID`, v: `orderId`, htmlName: `orderId`, id: `orderId` },
        { l: `Full Name Used`, v: `fullName`, htmlName: `fullName`, id: `fullName` },
        { l: `Email Used`, v: `email`, id: `email`, htmlName: `city` },
        {
            l: `Subtotal (before tax and shipping)`,
            v: `subTotal`,
            id: `subTotal`,
            regex: /[^.0-9]/g,
            htmlName: `subTotal`,
            pre: `$`,
        },
    ],
    validator: Yup.object().shape({
        orderId: Yup.string().required(`Please enter the Order ID`),
        fullName: Yup.string().required(`Please enter the full name associated with the order`),
        email: Yup.string()
            .matches(/^\S+@\S+\.\S+$/, `Please enter a valid email address`)
            .required(`Please enter the email address associated with the order`),
        subTotal: Yup.string()
            .matches(/^\$?\d+(,\d{3})*\.?[0-9]?[0-9]?$/, `Please enter a valid amount`)
            .required(`Please enter the order subtotal`),
    }),
};

export const Button = styled(`button`)({
    padding: `12px 25px`,
    width: 150,
    color: `white`,
    fontWeight: 400,
    fontSize: 16,
    textAlign: `center`,
    cursor: `pointer`,
    border: `1px solid #ddd`,
    borderRadius: 4,
    margin: `14px 0 10px`,
    background: `#fbcdcd`,
});

export const Chevron = styled(`span`)<{ top?: string | number }>(({ top }) => ({
    margin: `0 0 0 5px`,
    verticalAlign: `middle`,

    position: `absolute`,
    top,

    "&::before": {
        borderStyle: `solid`,
        borderWidth: `2px 2px 0 0`,
        verticalAlign: `middle`,
        content: `""`,
        display: `inline-block`,
        height: 8,
        left: 4,
        position: `relative`,
        top: 4,
        transform: `rotate(135deg)`,
        width: 8,
    },
}));
